import { useCallback, useEffect, useRef, useState } from "react";
import { useMap } from "react-map-gl/mapbox";
import { cn } from "~/lib/utils";
import { Icon } from "~/components/ui/icons/icon";
import { ControlGroup } from "./ControlGroup";

/**
 * Compass control that shows map orientation and resets to north on click.
 * Rotates based on the map's current bearing.
 */
export function CompassControl() {
  const { current: map } = useMap();
  const [rotation, setRotation] = useState(0);
  const prevBearingRef = useRef(0);

  useEffect(() => {
    if (!map) return;

    const handleMove = () => {
      const newBearing = map.getBearing();
      const prevBearing = prevBearingRef.current;

      // Calculate the shortest delta, handling the ±180° wrap
      let delta = newBearing - prevBearing;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;

      prevBearingRef.current = newBearing;
      setRotation((prev) => prev + delta);
    };

    handleMove();
    map.on("move", handleMove);
    return () => {
      map.off("move", handleMove);
    };
  }, [map]);

  const handleClick = useCallback(() => {
    map?.easeTo({ bearing: 0, pitch: 0 });
  }, [map]);

  return (
    <ControlGroup>
      <button
        type="button"
        className={cn("flex items-center justify-center p-1", "text-muted-foreground hover:bg-muted transition-colors")}
        onClick={handleClick}
        aria-label="Reset to north"
        title="Reset to north"
      >
        <Icon
          i="compass-needle"
          className="size-5 transition-transform duration-100"
          style={{ transform: `rotate(${-rotation}deg)` }}
        />
      </button>
    </ControlGroup>
  );
}
